import React from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import {
  ArrowLeft,
  ClipboardCheck,
  GraduationCap,
  Layers,
  MessageSquare,
} from "lucide-react";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import { resolveOrganizationId } from "../../../Utils/organizationSession";
import "./TrainingPortalPage.css";

const PORTAL_ACCENT = "rgb(8, 145, 178)";

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "P";
  if (parts[0].startsWith(".")) return parts[0].slice(0, 2).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function batchLabel(row) {
  if (row?.batch_code && row?.batch_name) {
    return `${row.batch_code} · ${row.batch_name}`;
  }
  return row?.batch_name || row?.batch_code || "—";
}

export default function TrainingPortalPage() {
  const { userData } = useAuthStore();
  const organizationId = resolveOrganizationId(userData);
  const [searchParams, setSearchParams] = useSearchParams();
  const programParam = searchParams.get("program") || "";
  const [loading, setLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState("");
  const [programs, setPrograms] = React.useState([]);
  const [tab, setTab] = React.useState("overview");

  const loadPortal = React.useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setErrorText("");
    try {
      const res = await axios.get(`${MAIN_URL}/api/ems/training-portal`, {
        headers: getApiHeaders(),
        params: { organization_id: organizationId },
      });
      setPrograms(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (err) {
      setPrograms([]);
      setErrorText(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load the training portal."
      );
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  React.useEffect(() => {
    loadPortal();
  }, [loadPortal]);

  React.useEffect(() => {
    setTab("overview");
  }, [programParam]);

  const selectedProgram = programs.find(
    (row) => String(row.organization_ems_training_program_id) === String(programParam)
  );

  const openProgram = (id) => {
    setSearchParams({ program: String(id) });
  };

  const clearProgram = () => {
    setSearchParams({});
  };

  if (loading && !programs.length) {
    return (
      <div className="ems-loading-screen">
        <CircularProgress size={28} />
      </div>
    );
  }

  if (errorText) {
    return (
      <div className="ems-training-portal">
        <p className="ems-training-portal-empty" style={{ color: "var(--ems-red)" }}>
          {errorText}
        </p>
      </div>
    );
  }

  if (programParam && selectedProgram) {
    return (
      <ProgramWorkspace
        program={selectedProgram}
        tab={tab}
        setTab={setTab}
        onBack={clearProgram}
      />
    );
  }

  return (
    <div className="ems-dashboard ems-fade-up ems-training-portal">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">Training Programs</h1>
          <p className="ems-page-sub">Open a program to see modules, assessments, and students.</p>
        </div>
      </div>
      <div className="ems-section-divider" />
      {programs.length === 0 ? (
        <p className="ems-training-portal-empty">No training programs found.</p>
      ) : (
        <div className="ems-training-portal-gallery">
          {programs.map((program) => (
              <button
                key={program.organization_ems_training_program_id}
                type="button"
                className="ems-card ems-training-portal-tile"
                onClick={() => openProgram(program.organization_ems_training_program_id)}
              >
                <span
                  className="ems-training-portal-avatar"
                  style={{ background: PORTAL_ACCENT }}
                >
                  {program.photo_url ? (
                    <img src={program.photo_url} alt="" />
                  ) : (
                    initials(program.training_program_name)
                  )}
                </span>
                <strong>{program.training_program_name}</strong>
                <span>
                  {program.training_program_code || "Program"}
                  {program.category_name ? ` · ${program.category_name}` : ""}
                </span>
              </button>
          ))}
        </div>
      )}
    </div>
  );
}

function interviewQaHref(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^https?:\/\//i.test(text)) return text;
  if (text.startsWith("//")) return `https:${text}`;
  if (/^(www\.)/i.test(text) || /^[a-z0-9][-a-z0-9.]*\.[a-z]{2,}(\/|$)/i.test(text)) {
    return `https://${text.replace(/^\/+/, "")}`;
  }
  return "";
}

function ProgramWorkspace({ program, tab, setTab, onBack }) {
  const navigate = useNavigate();
  const modules = Array.isArray(program.modules) ? program.modules : [];
  const programId = program.organization_ems_training_program_id;
  const interviewUrl = interviewQaHref(program.interview_qa_url);

  const openStudents = () => {
    if (programId == null || String(programId).trim() === "") return;
    const params = new URLSearchParams();
    params.set("training_program", String(programId));
    params.set("training_program_id", String(programId));
    navigate(`/students?${params.toString()}`);
  };

  const openInterviewQa = () => {
    if (!interviewUrl) return;
    window.open(interviewUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="ems-dashboard ems-fade-up ems-training-portal">
      <button type="button" className="ems-training-portal-back" onClick={onBack}>
        <ArrowLeft size={16} />
        All programs
      </button>

      <section className="ems-training-portal-hero">
        <span
          className="ems-training-portal-avatar ems-training-portal-avatar-lg"
          style={{ background: PORTAL_ACCENT }}
        >
          {program.photo_url ? (
            <img src={program.photo_url} alt="" />
          ) : (
            initials(program.training_program_name)
          )}
        </span>
        <h1>{program.training_program_name}</h1>
        <div className="ems-training-portal-chips">
          {program.training_program_code ? (
            <em>{program.training_program_code}</em>
          ) : null}
          {program.category_name ? <em>{program.category_name}</em> : null}
        </div>
      </section>

      <div className="ems-training-portal-kpis">
        <article className="is-clickable" onClick={() => setTab("overview")}>
          <span className="is-teal"><Layers size={18} /></span>
          <b>{modules.length}</b>
          <small>Modules</small>
        </article>
        <article className="is-clickable" onClick={() => setTab("assessments")}>
          <span className="is-green"><ClipboardCheck size={18} /></span>
          <b>{program.assessment_count ?? 0}</b>
          <small>Assessments</small>
        </article>
        <article
          className={interviewUrl ? "is-clickable" : undefined}
          onClick={interviewUrl ? openInterviewQa : undefined}
          title={interviewUrl ? "Open interview Q/A" : "No interview Q/A link"}
        >
          <span className="is-sky"><MessageSquare size={18} /></span>
          <b>{interviewUrl ? 1 : 0}</b>
          <small>Interview Q/A</small>
        </article>
        <article
          className="is-clickable"
          onClick={openStudents}
          title="View students in this program"
        >
          <span className="is-gold"><GraduationCap size={18} /></span>
          <b>{program.student_count ?? 0}</b>
          <small>Students</small>
        </article>
      </div>

      <div className="ems-training-portal-tabs">
        {[
          { id: "overview", label: "Overview" },
          { id: "assessments", label: "Assessments" },
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            className={tab === item.id ? "is-active" : undefined}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "overview" ? (
        <section className="ems-training-portal-overview">
          <header>
            <h2>Modules</h2>
          </header>
          {modules.length ? (
            <ul className="ems-training-portal-coverage">
              {modules.map((module, index) => {
                const questions = Number(module.question_count || 0);
                return (
                  <li key={module.organization_ems_training_program_module_id}>
                    <span className="ems-training-portal-coverage-num">
                      {index + 1}.
                    </span>
                    <span className="ems-training-portal-coverage-name">
                      {module.module_name || "Module"}
                    </span>
                    <span className="ems-training-portal-coverage-q">{questions} Q</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p>No modules in this program.</p>
          )}
        </section>
      ) : null}

      {tab === "assessments" ? (
        <section className="ems-training-portal-panel">
          {program.assessments?.length ? (
            <div className="ems-training-portal-given">
              <div className="ems-training-portal-given-head">
                <span>Assessment</span>
                <span>Given to batch</span>
                <span>Date</span>
              </div>
              {program.assessments.flatMap((item) => {
                const schedules = Array.isArray(item.schedules) ? item.schedules : [];
                if (!schedules.length) {
                  return [
                    <div
                      key={item.organization_ems_assessment_id}
                      className="ems-training-portal-given-row"
                    >
                      <strong>{item.assessment_name || "Assessment"}</strong>
                      <span>Not given to a batch yet</span>
                      <span>—</span>
                    </div>,
                  ];
                }
                return schedules.map((row) => (
                  <div
                    key={
                      row.organization_ems_assessment_schedule_id ||
                      `${item.organization_ems_assessment_id}-${row.batch_id}-${row.scheduled_date}`
                    }
                    className="ems-training-portal-given-row"
                  >
                    <strong>{item.assessment_name || row.assessment_name || "Assessment"}</strong>
                    <span>{batchLabel(row)}</span>
                    <span>{formatDate(row.scheduled_date)}</span>
                  </div>
                ));
              })}
            </div>
          ) : (
            <p className="ems-training-portal-empty">No assessments in this program.</p>
          )}
        </section>
      ) : null}
    </div>
  );
}
